import { Injectable } from '@angular/core';
import { ApplicationState, getUserProduct } from '../store/reducers';
import { PlcCodes } from '@dch/products-business-logic';
import { Store } from '@ngrx/store';
import { TranslationService } from '@proximus/ng-common';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class EwebTokenService {

  constructor(
    private readonly store: Store<ApplicationState>,
    private readonly translationService: TranslationService
  ) {
  }

  /**
   * Retrieves the ewebToken for a given serviceId from user products
   * @param packs - Array of packs from user products
   * @param outOfPacks - Array of outOfPacks from user products
   * @param serviceId - The service ID (accessNumber) to search for
   * @returns The ewebToken if found, null otherwise
   */
  getEwebTokenByServiceId(packs: any[], outOfPacks: any[], serviceId: string): string | null {
    if (!serviceId) {
      return null;
    }

    // Search in packs
    for (const pack of packs || []) {
      const coreProducts = pack?.coreProducts || [];
      for (const product of coreProducts) {
        if (product && product.accessNumber === serviceId) {
          return product.ewebToken || pack.ewebToken || null;
        }
      }
    }

    // Search in outOfPacks
    for (const outOfPack of outOfPacks || []) {
      const coreProducts = outOfPack?.coreProducts || [];
      for (const product of coreProducts) {
        if (product && product.accessNumber === serviceId) {
          return product.ewebToken || outOfPack.ewebToken || null;
        }
      }
    }

    return null;
  }

  getEwebTokenByPlc(packs: any[], outOfPacks: any[], plc: PlcCodes): string | null {
    if (!plc) {
      return null;
    }

    // Search in packs
    for (const pack of packs || []) {
      const coreProducts = pack?.coreProducts || [];
      for (const product of coreProducts) {
        if (product && product.plc === plc) {
          return product.ewebToken || pack.ewebToken || null;
        }
      }
    }

    // Search in outOfPacks
    for (const outOfPack of outOfPacks || []) {
      const coreProducts = outOfPack?.coreProducts || [];
      for (const product of coreProducts) {
        if (product && product.plc === plc) {
          return product.ewebToken || outOfPack.ewebToken || null;
        }
      }
    }

    return null;
  }

  getRedirectToEshareOptionFlowUrl(plc: PlcCodes, mpcuid?: string, serviceId?: string, aiFlow?: boolean): Observable<string> {
    console.log('getRedirectToEshareOptionFlowUrl called with plc:', plc, 'mpcuid:', mpcuid, 'serviceId:', serviceId, 'aiFlow:', aiFlow);
    return this.store.select(getUserProduct).pipe(
      map((data) => {
        if (!data?.packs?.length && !data?.outOfPacks?.length) {
          return null;
        }

        let ewebToken = serviceId ? this.getEwebTokenByServiceId(data.packs, data.outOfPacks, serviceId) : null;
        if (!ewebToken) {
          ewebToken = this.getEwebTokenByPlc(data.packs, data.outOfPacks, plc);
        }

        if (!ewebToken) {
          return null;
        }

        const redirectUrl = this.getOptionFlowRedirectUrl(plc);
        if (!redirectUrl) {
          return null;
        }

        return this.buildOptionFlowUrl(redirectUrl, ewebToken, mpcuid, aiFlow);
      })
    );
  }

  private getOptionFlowRedirectUrl(plc: PlcCodes): string | null {
    switch (plc) {
      case PlcCodes.TV:
        return this.translationService.getTranslatedValueFor('eshare.tv.optionFlowTvRedirectUrl');
      case PlcCodes.MV:
        return this.translationService.getTranslatedValueFor('eshare.mobile.optionFlowMobileRedirectUrl');
      default:
        return null;
    }
  }

  private buildOptionFlowUrl(redirectUrl: string, ewebToken: string, mpcuid?: string, aiFlow?: boolean): string {
    const separator = redirectUrl?.includes('?') ? '&' : '?';
    const mpcuidParam = mpcuid ? `&mpcuid=${mpcuid}` : '';
    const aiFlowParam = aiFlow ? '&aiFlow=true' : '';
    return `${redirectUrl}${separator}token=${ewebToken}${mpcuidParam}${aiFlowParam}`;
  }
}

